/**
 * Carte d'affichage d'une section pdf-annexe dans l'editeur de rapport.
 * Montre le nom du fichier, sa taille, un bouton renommer et un bouton supprimer.
 * La fusion dans le PDF final est gérée par pdf-generator (au moment de l'export).
 */

import { useState, useEffect } from "react";
import { FileText, Trash2, Pencil, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLiveQuery } from "@/core/hooks/useLiveQuery";
import { offlineDb } from "@/core/offline/db";
import type { CustomSection } from "@/core/offline/db";

interface PdfAnnexeCardProps {
  section: CustomSection;
  sectionNumber: number;
  onDelete: () => void;
  onRename: (newName: string) => void;
}

export function PdfAnnexeCard({ section, sectionNumber, onDelete, onRename }: PdfAnnexeCardProps) {
  const annexe = useLiveQuery(
    async () =>
      section.pdfAnnexeId
        ? await offlineDb.rapportAnnexes.get(section.pdfAnnexeId)
        : undefined,
    [section.pdfAnnexeId]
  );

  const [editingName, setEditingName] = useState(false);
  const [localName, setLocalName] = useState(annexe?.fileName ?? section.title);

  // Sync localName quand annexe charge
  useEffect(() => {
    if (annexe?.fileName) setLocalName(annexe.fileName);
  }, [annexe?.fileName]);

  function commitRename() {
    if (localName.trim() && localName !== annexe?.fileName) {
      onRename(localName.trim());
    }
    setEditingName(false);
  }

  return (
    <section id={`section-custom-${section.id}`} className="mb-10">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
          {sectionNumber}
        </span>
        <h2 className="text-xl font-bold">Annexe PDF</h2>
      </div>

      <div className="rounded-xl border border-border bg-card p-4 flex items-start gap-3">
        <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <FileText className="size-5 text-primary" />
        </div>

        <div className="flex-1 min-w-0">
          {editingName ? (
            <div className="flex items-center gap-1.5">
              <Input
                autoFocus
                value={localName}
                onChange={(e) => setLocalName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename();
                  if (e.key === "Escape") {
                    setLocalName(annexe?.fileName ?? section.title);
                    setEditingName(false);
                  }
                }}
                className="h-8 text-sm"
              />
              <button
                onClick={commitRename}
                className="w-7 h-7 flex items-center justify-center rounded hover:bg-muted"
                title="Valider"
              >
                <Check className="size-3.5" />
              </button>
              <button
                onClick={() => {
                  setLocalName(annexe?.fileName ?? section.title);
                  setEditingName(false);
                }}
                className="w-7 h-7 flex items-center justify-center rounded hover:bg-muted"
                title="Annuler"
              >
                <X className="size-3.5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <p className="font-medium text-sm truncate">{annexe?.fileName ?? section.title}</p>
              <button
                onClick={() => setEditingName(true)}
                className="shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-muted text-muted-foreground"
                title="Renommer"
              >
                <Pencil className="size-3" />
              </button>
            </div>
          )}

          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
            {annexe ? (
              <>
                <span>{formatBytes(annexe.fileSize)}</span>
                <span>
                  {annexe.syncStatus === "synced" ? "Synchronisé" : "Local"}
                </span>
              </>
            ) : (
              <span className="text-destructive">Fichier introuvable</span>
            )}
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="shrink-0 text-destructive hover:bg-destructive/10"
          title="Supprimer cette annexe"
        >
          <Trash2 className="size-4" />
        </Button>
      </div>
    </section>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}
